import { EnvironmentOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons'
import { PageLang } from '../pageLang'

const CONTENT: Record<
  PageLang,
  {
    eyebrow: string
    headline: string
    addressLabel: string
    officeName: string
    addressLine: string
    mapsLink: string
    mobileLabel: string
    emailLabel: string
    connectWithUs: string
  }
> = {
  EN: {
    eyebrow: 'Get in touch',
    headline: 'Contact us',
    addressLabel: 'Address',
    officeName: 'HAUP Head Office',
    addressLine:
      '128/21 3rd Floor, Phyathai Plaza Building, Phyathai Road Thung Phaya Thai Subdistrict, Ratchathewi, Bangkok 10400',
    mapsLink: 'View on Google Maps →',
    mobileLabel: 'Mobile',
    emailLabel: 'Email',
    connectWithUs: 'Connect with us on :',
  },
  TH: {
    eyebrow: 'ติดต่อเรา',
    headline: 'ติดต่อเรา',
    addressLabel: 'ที่อยู่',
    officeName: 'สำนักงานใหญ่ HAUP',
    addressLine: '128/21 ชั้น 3 อาคารพญาไทพลาซ่า ถนนพญาไท แขวงทุ่งพญาไท เขตราชเทวี กรุงเทพฯ 10400',
    mapsLink: 'ดูบน Google Maps →',
    mobileLabel: 'เบอร์โทรศัพท์',
    emailLabel: 'อีเมล',
    connectWithUs: 'ติดตามเราได้ที่ :',
  },
}

const SOCIAL_ICONS = {
  line: 'https://ts-upload.sgp1.digitaloceanspaces.com/cms/5ZUpHjQdM2R8pvqmnTvML7hgmKywze2gBfAMsHLM8qfGHhFsFoHzgTu2GpCiGQDCdDQEQczBfpvX6xBG9mc1CuDe.png',
  facebook:
    'https://ts-upload.sgp1.digitaloceanspaces.com/cms/3ojYSQ97EBqNNFgwFE7rkLrBUsmDCvKgnimsJVhiZAMgFPzuCtEAdFrDXggvKLidHgMKUMpq2xedA3zHf47ysrqm.png',
  instagram:
    'https://ts-upload.sgp1.digitaloceanspaces.com/cms/3J2mkwUhuNwmiMGbCqX7WG6Pnf3Ebto1TJMGBnjYwAy7BvPTB43HPVtk6QFhSAGJPDXUqpMcSYwPAzgE1RtfET3X.png',
  tiktok:
    'https://ts-upload.sgp1.digitaloceanspaces.com/cms/2Q2dZNqEHpZVcf23UAw3w7HL3fZXwNiHxMmBwizyyURmw1a7eJ9bwrmhCVDvcZGNgUCNPuTh3tmbSjHLDU3WSFH7.png',
}

const IconBox = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      width: '40px',
      height: '40px',
      borderRadius: '10px',
      backgroundColor: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {children}
  </div>
)

const ContactRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
}) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '18px' }}>
    <IconBox>{icon}</IconBox>
    <div>
      <div style={{ fontSize: '12px', color: '#8C97A7', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '14px', fontWeight: 700, color: '#212427', lineHeight: '1.5' }}>
        {value}
      </div>
    </div>
  </div>
)

export interface ContactFooterSectionProps {
  lang: PageLang
}

export const ContactFooterSection = ({ lang }: ContactFooterSectionProps) => {
  const t = CONTENT[lang]
  return (
    <div
      style={{
        padding: '32px 16px 32px',
        backgroundColor: '#F4F5F9',
      }}
    >
      <div
        style={{
          display: 'inline-block',
          backgroundColor: '#EAF1FF',
          color: '#0048B3',
          fontSize: '13px',
          fontWeight: 600,
          padding: '6px 16px',
          borderRadius: '16px',
          marginBottom: '16px',
        }}
      >
        {t.eyebrow}
      </div>
      <h2 style={{ fontSize: '26px', fontWeight: 600, color: '#212427', margin: '0 0 20px' }}>
        {t.headline}
      </h2>

      <ContactRow
        icon={<EnvironmentOutlined style={{ color: '#0048B3', fontSize: '18px' }} />}
        label={t.addressLabel}
        value={
          <>
            {t.officeName}
            <div style={{ fontSize: '13px', fontWeight: 400, color: '#667085', marginTop: '4px' }}>
              {t.addressLine}
            </div>
            <a
              href="https://www.google.com/maps/search/?api=1&query=128%2F21+Phyathai+Plaza+Building%2C+Phyathai+Road%2C+Ratchathewi%2C+Bangkok+10400"
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-block',
                color: '#0048B3',
                fontSize: '13px',
                fontWeight: 600,
                marginTop: '8px',
              }}
            >
              {t.mapsLink}
            </a>
          </>
        }
      />
      <ContactRow
        icon={<PhoneOutlined style={{ color: '#0048B3', fontSize: '18px' }} />}
        label={t.mobileLabel}
        value="02 113 1155"
      />
      <ContactRow
        icon={<MailOutlined style={{ color: '#0048B3', fontSize: '18px' }} />}
        label={t.emailLabel}
        value="service@haupcar.com"
      />

      <div style={{ fontSize: '14px', color: '#212427', marginBottom: '10px' }}>
        {t.connectWithUs}
      </div>
      <div style={{ display: 'flex', gap: '10px' }}>
        <a href="https://page.line.me/630rspua" target="_blank" rel="noreferrer">
          <IconBox>
            <img src={SOCIAL_ICONS.line} alt="LINE" style={{ width: '24px', height: '24px' }} />
          </IconBox>
        </a>
        <a href="https://www.facebook.com/HAUPCAR/" target="_blank" rel="noreferrer">
          <IconBox>
            <img
              src={SOCIAL_ICONS.facebook}
              alt="Facebook"
              style={{ width: '18px', height: '18px', objectFit: 'contain' }}
            />
          </IconBox>
        </a>
        <a href="https://www.instagram.com/haupcar/" target="_blank" rel="noreferrer">
          <IconBox>
            <img
              src={SOCIAL_ICONS.instagram}
              alt="Instagram"
              style={{ width: '20px', height: '20px' }}
            />
          </IconBox>
        </a>
        <a href="https://www.tiktok.com/@haupcar" target="_blank" rel="noreferrer">
          <IconBox>
            <img src={SOCIAL_ICONS.tiktok} alt="TikTok" style={{ width: '20px', height: '20px' }} />
          </IconBox>
        </a>
      </div>
    </div>
  )
}

export default ContactFooterSection
